import { useState, useCallback, useEffect } from 'react';

import {
  EMAIL_REGEX,
  USERNAME_REGEX,
  PHONE_REGEX,
  ONLY_NUMBER_REGEX,
} from 'constants/regex';

const FIELD_TYPE_REGEX = {
  number: {
    regex: ONLY_NUMBER_REGEX,
    message: 'Please enter only numbers',
  },
  phone: {
    regex: PHONE_REGEX,
    message: 'Phone number can only contain numbers, -/+ characters',
  },
};

const useFormValidation = (initialValues, validationSchema) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = useCallback((value, fieldSchema) => {
    if (!fieldSchema) return null;

    if (fieldSchema.required && value === '') {
      return fieldSchema.required;
    }

    if (fieldSchema.type === 'email' && value !== '') {
      if (!EMAIL_REGEX.test(value)) {
        return 'Please enter a valid email';
      }
    }

    if (fieldSchema.type === 'username' && value !== '') {
      if (!USERNAME_REGEX.test(value)) {
        return 'This value may contain only letters, numbers, and @/./+/-/_ characters';
      }
    }

    if (fieldSchema.type === 'phone' && value !== '') {
      if (!PHONE_REGEX.test(value)) {
        return 'Phone number can only contain numbers and hyphens';
      }
    }

    if (fieldSchema.type === 'number' && value !== '') {
      const numberValue = Number(value);
      if (isNaN(numberValue)) {
        return fieldSchema.invalidNumber || 'Please enter a number';
      }
    }

    return null;
  }, []);

  const validate = useCallback(
    (fieldValues, touchedFields) => {
      const validationErrors = {};

      Object.keys(validationSchema).forEach(field => {
        // touched 필드만 check
        if (touchedFields[field]) {
          const value = fieldValues[field];
          const fieldSchema = validationSchema[field];
          const error = validateField(value, fieldSchema);

          if (error) {
            validationErrors[field] = error;
          }
        }
      });

      return validationErrors;
    },
    [validationSchema, validateField],
  );

  useEffect(() => {
    const validationErrors = validate(values, touched);
    setErrors(validationErrors);
  }, [values, touched, validate]);

  const handleChange = useCallback(
    (field, value) => {
      const fieldSchema = validationSchema[field];
      const fieldType = fieldSchema?.type;
      const regexConfig = FIELD_TYPE_REGEX[fieldType];

      if (regexConfig) {
        if (value === '') {
          setValues(prev => ({ ...prev, [field]: value }));
          setTouched(prev => ({ ...prev, [field]: true }));
          return;
        }

        if (!regexConfig.regex.test(value)) {
          return;
        }
      }

      setValues(prev => ({ ...prev, [field]: value }));
      setTouched(prev => ({ ...prev, [field]: true }));
    },
    [validationSchema],
  );

  const handleBlur = useCallback(field => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const validateForm = useCallback(() => {
    const validationErrors = {};

    Object.keys(validationSchema).forEach(field => {
      const value = values[field];
      const fieldSchema = validationSchema[field];
      const error = validateField(value, fieldSchema);

      if (error) {
        validationErrors[field] = error;
      }
    });

    const allTouched = Object.keys(validationSchema).reduce(
      (acc, field) => ({
        ...acc,
        [field]: true,
      }),
      {},
    );

    setErrors(validationErrors);
    setTouched(allTouched);

    return Object.keys(validationErrors).length === 0;
  }, [values, validationSchema, validateField]);

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  const isValid = Object.keys(validationSchema)
    .filter(field => validationSchema[field].required)
    .every(field => {
      const value = values[field];
      const fieldSchema = validationSchema[field];
      return !validateField(value, fieldSchema);
    });

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  };
};

export default useFormValidation;
